import { useMemo, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { ColDef, ICellRendererParams } from 'ag-grid-enterprise'
import { isWeekend } from 'date-fns'
import { DateRange } from 'react-day-picker'

import { getDatesInDayRange, getTMinus, getTMinus1, toISO8601 } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { useConfirmModal } from '@fortem-monorepo/react-spa'
import { DatePicker } from '@fortem-monorepo/react-ui'
import { DateRangePicker } from '@fortem-monorepo/react-ui'
import { LoadingButton } from '@fortem-monorepo/react-ui'
import { DataTableFacetedFilter } from '@fortem-monorepo/react-ui'

import { FeedsStatusCellRenderer } from '@/ag-grid/cell-renderers/FeedsStatusCellRenderer'
import { CanAccess } from '@/components/CanAccess'
import { Page } from '@/components/Page'
import { FEEDS } from '@/constants/links'
import { PERMISSIONS } from '@/constants/permissions'
import { useRunICGuidelinesMutation } from '@/react-query/mutations/ic-guidelines'
import { useFunds } from '@/react-query/queries/funds/funds'

export interface FeedsICGuidelinesPageProps {}

export const FeedsICGuidelinesPage: React.FC<FeedsICGuidelinesPageProps> = ({}) => {
  const api = useApi()
  const [dates, setDates] = useState<DateRange>({
    from: getTMinus(5),
    to: getTMinus1(),
  })
  const [selectedDate, setSelectedDate] = useState(dates.to!)
  const [selectedFunds, setSelectedFunds] = useState<Array<string>>([])

  const params = {
    st_dt: dates.from ? toISO8601(dates.from!) : '',
    ed_dt: dates.from ? toISO8601(dates.to ?? dates.from!) : '',
  }

  const icGuidelinesQuery = useQuery({
    queryKey: ['feeds', 'ic-guidelines', params] as const,
    queryFn: () => api.feeds.getICGuidelines(params),

    enabled: !!dates.from,
  })

  const runReportMutation = useRunICGuidelinesMutation()

  const datesList = useMemo(() => {
    if (!dates.from) return []
    return getDatesInDayRange(dates.from!, dates.to, true).sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime(),
    )
  }, [dates.from, dates.to])

  const { data: funds } = useFunds()

  const fundsOptions = useMemo(() => {
    return funds
      .filter((fund) => fund.active && fund.agent_name === 'NT')
      .map((fund) => ({
        label: fund.fund_name,
        value: fund.fund_name,
      }))
  }, [funds])

  const { Dialog: RunReportDialog, withConfirmation } = useConfirmModal({
    title: 'Run Report',
    description: `You are about to run the report for ${selectedFunds.join(', ')} on ${toISO8601(selectedDate!)}`,
  })

  const inputsDisabled = icGuidelinesQuery.isFetching

  return (
    <>
      <Page metadata={{ title: 'PM Posmon' }} permissions={FEEDS.IC_GUIDELINES.permissions}>
        <CanAccess permissions={[PERMISSIONS.RUN_IC_GUIDELINES]}>
          <HeaderConfigBar>
            <div className="flex gap-2 items-center">
              <DatePicker
                size={'sm'}
                selected={selectedDate}
                onSelect={(date) => {
                  setSelectedDate(date!)
                }}
                buttonDisabled={inputsDisabled}
                disabled={(date) =>
                  isWeekend(date) ||
                  (date.getDate() == selectedDate.getDate() &&
                    date.getMonth() == selectedDate.getMonth())
                }
              />
              <DataTableFacetedFilter
                disabled={inputsDisabled}
                toggleAll={() =>
                  setSelectedFunds((prev) =>
                    prev.length < funds.length ? fundsOptions.map((opt) => opt.value) : [],
                  )
                }
                size={'sm'}
                title={'Funds'}
                values={selectedFunds}
                setValues={(values) => values && setSelectedFunds(values)}
                options={fundsOptions}
              />
              <LoadingButton
                size="sm"
                onClick={async () =>
                  withConfirmation(() => {
                    runReportMutation.mutate({
                      funds: selectedFunds,
                      value_date: toISO8601(selectedDate!),
                    })
                    // TODO: Invalidate Feeds IC Guidelines
                    // Currently can't do this as this is done using background tasks
                    // in FastAPI
                  })
                }
                disabled={!selectedFunds.length || inputsDisabled}
              >
                Run Report
              </LoadingButton>
            </div>
          </HeaderConfigBar>
        </CanAccess>
        <HeaderConfigBar>
          <div className="flex gap-2 items-center">
            <DateRangePicker
              size="sm"
              date={dates}
              setDate={(range) =>
                setDates({
                  from: range.from,
                  to: range.to ?? range.from,
                })
              }
              disabled={(date) => isWeekend(date)}
              buttonDisabled={inputsDisabled}
            />
          </div>
        </HeaderConfigBar>

        <div className="flex-1 bg-black/20">
          <Widget exposed className={'h-full'}>
            <WidgetHeaderWithQuery query={icGuidelinesQuery} title="Data By Fund" />
            <WidgetContent loading={icGuidelinesQuery?.isLoading}>
              <CustomAgGrid
                domLayout={'normal'}
                rowData={icGuidelinesQuery.data ?? ([] as unknown as any)}
                columnDefs={createColDefs([
                  {
                    field: 'fund_name',
                    rowGroup: true,
                    hide: true,
                  },
                  { field: 'label', width: 140 },
                  ...datesList.map(
                    (date) =>
                      ({
                        field: toISO8601(date),
                        cellRenderer: (params: ICellRendererParams) => {
                          return !params.node.group && <FeedsStatusCellRenderer {...params} />
                        },
                      }) satisfies ColDef,
                  ),
                ])}
              />
            </WidgetContent>
          </Widget>
        </div>
      </Page>
      <RunReportDialog />
    </>
  )
}
